import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormFeedback,
  Input,
  Label,
} from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { CanView } from "components/Common/CanView"
import ThemeModal from "components/Common/ThemeModal"
import UserAccountEditor from "components/Common/UserAccountEditor"
import { ACCESS } from "constants/accessPermissions"
import { useAuth } from "hooks/authHook"
import {
  useGrantEmployeeAccess,
  useImpersonateEmployee,
  useResetEmployeePassword,
  useRevokeEmployeeAccess,
  useUpdateEmployeeRoles,
} from "hooks/employeeHook"
import { useRoles } from "hooks/useRolePermission"
import { getToken, setOriginalAuthUser, setOriginalToken, setToken } from "utils/helpersUtils"

const buildRoleOptions = roles =>
  (roles ?? [])
    .filter(r => !r.is_system)
    .map(r => ({ value: r.id, label: r.name }))

const generatePassword = (length = 12) => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ"
  const lower = "abcdefghjkmnpqrstuvwxyz"
  const digit = "23456789"
  const special = "@$!%*?&"
  const all = upper + lower + digit + special
  const chars = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digit[Math.floor(Math.random() * digit.length)],
    special[Math.floor(Math.random() * special.length)],
  ]
  for (let i = chars.length; i < length; i++) {
    chars.push(all[Math.floor(Math.random() * all.length)])
  }
  for (let i = chars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join("")
}

const selectStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#556ee6"
      : state.isFocused
      ? "#e9ecef"
      : "#fff",
    color: state.isSelected ? "#fff" : "#495057",
  }),
  menu: provided => ({ ...provided, zIndex: 9999 }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: "#556ee6",
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#fff",
  }),
  multiValueRemove: provided => ({
    ...provided,
    color: "#fff",
    ":hover": { backgroundColor: "#3b5bdb", color: "#fff" },
  }),
}

const SYSTEM_ACCESS_BLOCKED_STATUSES = ["inactive", "resigned", "terminated"]

const humanizeStatus = status =>
  status
    ? status
        .split("_")
        .join(" ")
        .replace(/\b\w/g, char => char.toUpperCase())
    : "Unknown"

const SystemAccessCard = ({ employee, onChange }) => {
  const navigate = useNavigate()
  const [manualOpen, setManualOpen] = useState(false)
  const [autoOpen, setAutoOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const [updateRolesOpen, setUpdateRolesOpen] = useState(false)
  const [resetPwOpen, setResetPwOpen] = useState(false)

  // Credentials returned from grant-access or reset-password (shown ONCE)
  const [credentialsModal, setCredentialsModal] = useState(null)
  const [isImpersonatingSelf, setIsImpersonatingSelf] = useState(false)

  const { data: rolesResp } = useRoles({ paginate: false }, true)
  const { user: authUser, isImpersonating: hasActiveImpersonation } = useAuth()
  const roleOptions = useMemo(
    () => buildRoleOptions(rolesResp?.data?.data ?? rolesResp?.data ?? []),
    [rolesResp]
  )

  const { mutateAsync: grantAccess, isPending: granting } =
    useGrantEmployeeAccess()
  const { mutateAsync: revokeAccess, isPending: revoking } =
    useRevokeEmployeeAccess()
  const { mutateAsync: updateRoles, isPending: updatingRoles } =
    useUpdateEmployeeRoles()
  const { mutateAsync: resetPassword, isPending: resettingPw } =
    useResetEmployeePassword()
  const { mutateAsync: impersonateEmployee } = useImpersonateEmployee()

  const hasLogin = !!employee?.user_id
  const linkedUser = employee?.user
  const linkedRoles = linkedUser?.roles ?? []
  const isRevoked = hasLogin && !!linkedUser?.deleted_at
  const isStatusBlocked = SYSTEM_ACCESS_BLOCKED_STATUSES.includes(employee?.status)
  const statusLabel = humanizeStatus(employee?.status)

  // Manual onboarding form
  const manualFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
      password: "",
      email: employee?.email || "",
      role_ids: [],
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .trim()
        .min(3, "Min 3 characters")
        .max(50)
        .matches(/^[a-zA-Z0-9_]+$/, "Letters, numbers, underscore only")
        .required("Username is required"),
      password: Yup.string().min(6, "Min 6 characters").required("Password is required"),
      email: Yup.string().email("Invalid email").nullable(),
      role_ids: Yup.array().of(Yup.number()).min(1, "Pick at least one role"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        const res = await grantAccess({
          id: employee.id,
          mode: "manual",
          username: values.username,
          password: values.password,
          email: values.email || undefined,
          role_ids: values.role_ids,
        })
        toast.success(res?.message ?? "System access granted.")
        manualFormik.resetForm()
        setManualOpen(false)
        setCredentialsModal(res?.data?.credentials ?? null)
        onChange?.()
      } catch (error) {
        const msg =
          error?.response?.data?.message ?? "Failed to grant system access."
        toast.error(msg)
      }
    },
  })

  // Auto onboarding form (only role selection)
  const autoFormik = useFormik({
    enableReinitialize: true,
    initialValues: { role_ids: [] },
    validationSchema: Yup.object({
      role_ids: Yup.array().of(Yup.number()).min(1, "Pick at least one role"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        const res = await grantAccess({
          id: employee.id,
          mode: "auto",
          role_ids: values.role_ids,
        })
        toast.success(res?.message ?? "System access granted (auto).")
        autoFormik.resetForm()
        setAutoOpen(false)
        setCredentialsModal(res?.data?.credentials ?? null)
        onChange?.()
      } catch (error) {
        const msg =
          error?.response?.data?.message ?? "Failed to auto-generate login."
        toast.error(msg)
      }
    },
  })

  // Restore access form (revoked user — only role selection needed)
  const restoreFormik = useFormik({
    enableReinitialize: true,
    initialValues: { role_ids: [] },
    validationSchema: Yup.object({
      role_ids: Yup.array().of(Yup.number()).min(1, "Pick at least one role"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        const res = await grantAccess({
          id: employee.id,
          mode: "auto",
          role_ids: values.role_ids,
        })
        toast.success(res?.message ?? "System access restored.")
        restoreFormik.resetForm()
        setRestoreOpen(false)
        setCredentialsModal(res?.data?.credentials ?? null)
        onChange?.()
      } catch (error) {
        const msg =
          error?.response?.data?.message ?? "Failed to restore system access."
        toast.error(msg)
      }
    },
  })

  // Update roles form
  const updateRolesFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      role_ids: linkedRoles.map(r => r.id),
    },
    validationSchema: Yup.object({
      role_ids: Yup.array().of(Yup.number()).min(1, "Pick at least one role"),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        const res = await updateRoles({
          id: employee.id,
          role_ids: values.role_ids,
        })
        toast.success(res?.message ?? "Roles updated.")
        setUpdateRolesOpen(false)
        onChange?.()
      } catch (error) {
        const msg =
          error?.response?.data?.message ?? "Failed to update roles."
        toast.error(msg)
        if (error?.response?.status === 422) {
          setUpdateRolesOpen(false)
          onChange?.()
        }
      }
    },
  })

  const handleRevoke = useCallback(async () => {
    try {
      const res = await revokeAccess(employee.id)
      toast.success(res?.message ?? "System access revoked.")
      setRevokeOpen(false)
      onChange?.()
    } catch (error) {
      const msg =
        error?.response?.data?.message ?? "Failed to revoke system access."
      toast.error(msg)
      if (error?.response?.status === 422) {
        setRevokeOpen(false)
        onChange?.()
      }
    }
  }, [employee, revokeAccess, onChange])

  const handleResetPassword = useCallback(async () => {
    try {
      const res = await resetPassword(employee.id)
      toast.success(res?.message ?? "Password reset successfully.")
      setResetPwOpen(false)
      setCredentialsModal(res?.data?.credentials ?? null)
      onChange?.()
    } catch (error) {
      const msg =
        error?.response?.data?.message ?? "Failed to reset password."
      toast.error(msg)
      if (error?.response?.status === 422) {
        setResetPwOpen(false)
        onChange?.()
      }
    }
  }, [employee, resetPassword, onChange])

  const handleImpersonate = useCallback(async () => {
    if (!employee?.id) return

    setIsImpersonatingSelf(true)

    try {
      const res = await impersonateEmployee(employee.id)
      if (res?.data?.access_token) {
        setOriginalToken(getToken())
        if (authUser) {
          setOriginalAuthUser(authUser)
        }
        setToken(res.data.access_token)
        toast.success(res.message || "Successfully impersonated user")
        window.location.replace("/dashboard")
      }
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to impersonate user.")
    } finally {
      setIsImpersonatingSelf(false)
    }
  }, [employee?.id, impersonateEmployee, authUser, navigate])

  const copyToClipboard = text => {
    if (!text) return
    navigator.clipboard?.writeText(text)
    toast.success("Copied to clipboard")
  }

  return (
    <>
      <Card>
        <CardBody>
          <CardTitle tag="h5" className="mb-3">
            <i className="bx bx-key me-1" />
            System Access
          </CardTitle>

          {!hasLogin && (
            <>
              <Alert color={isStatusBlocked ? "danger" : "warning"} className="mb-3">
                <i className={`bx ${isStatusBlocked ? "bx-block" : "bx-info-circle"} me-1`} />
                {isStatusBlocked
                  ? `System access is blocked while the employee status is ${statusLabel}. Change the employee status before granting login access.`
                  : "This employee has no system login. Grant access manually or auto-generate credentials."}
              </Alert>

              <CanView access={[ACCESS.EMPLOYEE.GRANT_ACCESS]}>
                <div className="d-grid gap-2">
                  <Button
                    color="primary"
                    onClick={() => setManualOpen(true)}
                    disabled={granting || isStatusBlocked}
                  >
                    <i className="bx bx-user-plus me-1" />
                    Manual Login
                  </Button>
                  <Button
                    color="success"
                    onClick={() => setAutoOpen(true)}
                    disabled={granting || isStatusBlocked}
                  >
                    <i className="bx bx-bolt-circle me-1" />
                    Auto-Generate Login
                  </Button>
                </div>
              </CanView>
            </>
          )}

          {isRevoked && (
            <>
              <Alert color="danger" className="mb-3">
                <i className="bx bx-block me-1" />
                {isStatusBlocked
                  ? `System access is blocked because the employee status is ${statusLabel}. Previous login details are shown below.`
                  : "System access has been revoked. Previous login details shown below."}
              </Alert>

              <div className="mb-3">
                <small className="text-muted d-block">Username</small>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="fw-semibold text-muted">
                    {linkedUser?.username}
                  </span>
                  <Badge color="danger" pill>Revoked</Badge>
                </div>
              </div>

              {linkedUser?.email && (
                <div className="mb-3">
                  <small className="text-muted d-block">Email</small>
                  <span className="text-muted">{linkedUser.email}</span>
                </div>
              )}

              <CanView access={[ACCESS.EMPLOYEE.GRANT_ACCESS]}>
                {!isStatusBlocked ? (
                  <div className="d-grid gap-2 mt-3">
                    <Button
                      color="success"
                      onClick={() => setRestoreOpen(true)}
                      disabled={granting}
                    >
                      <i className="bx bx-revision me-1" />
                      Restore Access
                    </Button>
                  </div>
                ) : null}
              </CanView>
            </>
          )}

          {hasLogin && !isRevoked && (
            <>
              {isStatusBlocked ? (
                <Alert color="danger" className="mb-3">
                  <i className="bx bx-error-circle me-1" />
                  This login should be blocked because the employee status is {statusLabel}. Revoke access immediately.
                </Alert>
              ) : null}

              <div className="mb-3">
                <small className="text-muted d-block">Username</small>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="fw-semibold">{linkedUser?.username}</span>
                  <Button
                    size="sm"
                    color="link"
                    onClick={() => copyToClipboard(linkedUser?.username)}
                    title="Copy"
                  >
                    <i className="bx bx-copy" />
                  </Button>
                </div>
              </div>

              {linkedUser?.email && (
                <div className="mb-3">
                  <small className="text-muted d-block">Email</small>
                  <span>{linkedUser.email}</span>
                </div>
              )}

              <div className="mb-3">
                <small className="text-muted d-block mb-1">Roles</small>
                {linkedRoles.length === 0 ? (
                  <span className="text-muted">No roles assigned</span>
                ) : (
                  <div className="d-flex flex-wrap gap-1">
                    {linkedRoles.map(r => (
                      <Badge key={r.id} color="primary" pill>
                        {r.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="d-grid gap-2 mt-3">
                <CanView access={[ACCESS.EMPLOYEE.GRANT_ACCESS]}>
                  <Button
                    color="warning"
                    onClick={handleImpersonate}
                    disabled={isImpersonatingSelf || hasActiveImpersonation || isStatusBlocked}
                  >
                    {isImpersonatingSelf ? (
                      <i className="bx bx-loader bx-spin me-1" />
                    ) : (
                      <i className="mdi mdi-account-convert me-1" />
                    )}
                    {hasActiveImpersonation ? "Already Impersonating" : "Impersonate Login"}
                  </Button>
                </CanView>
                <UserAccountEditor user={linkedUser} onSaved={onChange} />
                <CanView access={[ACCESS.EMPLOYEE.ASSIGN_ROLES]}>
                  <Button
                    color="info"
                    outline
                    onClick={() => setUpdateRolesOpen(true)}
                    disabled={isStatusBlocked}
                  >
                    <i className="bx bx-shield me-1" />
                    Update Roles
                  </Button>
                </CanView>
                <CanView access={[ACCESS.EMPLOYEE.GRANT_ACCESS]}>
                  <Button
                    color="warning"
                    outline
                    onClick={() => setResetPwOpen(true)}
                    disabled={isStatusBlocked}
                  >
                    <i className="bx bx-lock-open-alt me-1" />
                    Reset Password
                  </Button>
                </CanView>
                <CanView access={[ACCESS.EMPLOYEE.REVOKE_ACCESS]}>
                  <Button
                    color="danger"
                    outline
                    onClick={() => setRevokeOpen(true)}
                  >
                    <i className="bx bx-block me-1" />
                    Revoke Access
                  </Button>
                </CanView>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Manual Login Modal */}
      <ThemeModal
        isOpen={manualOpen}
        toggle={() => setManualOpen(false)}
        title="Create Login (Manual)"
      >
        <Form
          onSubmit={e => {
            e.preventDefault()
            manualFormik.handleSubmit()
          }}
        >
          <div className="mb-3">
            <Label>
              Username <span className="text-danger">*</span>
            </Label>
            <Input
              name="username"
              value={manualFormik.values.username}
              onChange={manualFormik.handleChange}
              invalid={!!manualFormik.errors.username}
            />
            {manualFormik.errors.username && (
              <FormFeedback>{manualFormik.errors.username}</FormFeedback>
            )}
          </div>

          <div className="mb-3">
            <Label>
              Password <span className="text-danger">*</span>
            </Label>
            <div className="d-flex gap-2">
              <div className="flex-grow-1">
                <Input
                  type="text"
                  name="password"
                  value={manualFormik.values.password}
                  onChange={manualFormik.handleChange}
                  invalid={!!manualFormik.errors.password}
                />
                {manualFormik.errors.password && (
                  <FormFeedback>{manualFormik.errors.password}</FormFeedback>
                )}
              </div>
              <Button
                color="secondary"
                outline
                type="button"
                title="Auto-generate password"
                onClick={() =>
                  manualFormik.setFieldValue("password", generatePassword())
                }
              >
                <i className="bx bx-refresh" />
              </Button>
            </div>
          </div>

          <div className="mb-3">
            <Label>Email</Label>
            <Input
              type="email"
              name="email"
              value={manualFormik.values.email}
              onChange={manualFormik.handleChange}
              invalid={!!manualFormik.errors.email}
            />
            {manualFormik.errors.email && (
              <FormFeedback>{manualFormik.errors.email}</FormFeedback>
            )}
          </div>

          <div className="mb-3">
            <Label>
              Roles <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={roleOptions}
              styles={selectStyles}
              value={roleOptions.filter(o =>
                manualFormik.values.role_ids.includes(o.value)
              )}
              onChange={selected =>
                manualFormik.setFieldValue(
                  "role_ids",
                  selected ? selected.map(s => s.value) : []
                )
              }
            />
            {manualFormik.errors.role_ids && (
              <div className="text-danger small mt-1">
                {manualFormik.errors.role_ids}
              </div>
            )}
          </div>

          <div className="text-end">
            <Button
              color="secondary"
              outline
              type="button"
              className="me-2"
              onClick={() => setManualOpen(false)}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={granting}>
              {granting ? "Granting..." : "Grant Access"}
            </Button>
          </div>
        </Form>
      </ThemeModal>

      {/* Auto Login Modal */}
      <ThemeModal
        isOpen={autoOpen}
        toggle={() => setAutoOpen(false)}
        title="Auto-Generate Login"
      >
        <Form
          onSubmit={e => {
            e.preventDefault()
            autoFormik.handleSubmit()
          }}
        >
          <Alert color="info">
            The system will generate a username and a strong password for{" "}
            <strong>{employee?.name}</strong>. Pick one or more roles below, and
            the credentials will be shown to you{" "}
            <strong>only once</strong> after creation.
          </Alert>

          <div className="mb-3">
            <Label>
              Roles <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={roleOptions}
              styles={selectStyles}
              value={roleOptions.filter(o =>
                autoFormik.values.role_ids.includes(o.value)
              )}
              onChange={selected =>
                autoFormik.setFieldValue(
                  "role_ids",
                  selected ? selected.map(s => s.value) : []
                )
              }
            />
            {autoFormik.errors.role_ids && (
              <div className="text-danger small mt-1">
                {autoFormik.errors.role_ids}
              </div>
            )}
          </div>

          <div className="text-end">
            <Button
              color="secondary"
              outline
              type="button"
              className="me-2"
              onClick={() => setAutoOpen(false)}
            >
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={granting}>
              {granting ? "Generating..." : "Generate & Grant"}
            </Button>
          </div>
        </Form>
      </ThemeModal>

      {/* Update Roles Modal */}
      <ThemeModal
        isOpen={updateRolesOpen}
        toggle={() => setUpdateRolesOpen(false)}
        title="Update Roles"
      >
        <Form
          onSubmit={e => {
            e.preventDefault()
            updateRolesFormik.handleSubmit()
          }}
        >
          <div className="mb-3">
            <Label>
              Roles <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={roleOptions}
              styles={selectStyles}
              value={roleOptions.filter(o =>
                updateRolesFormik.values.role_ids.includes(o.value)
              )}
              onChange={selected =>
                updateRolesFormik.setFieldValue(
                  "role_ids",
                  selected ? selected.map(s => s.value) : []
                )
              }
            />
            {updateRolesFormik.errors.role_ids && (
              <div className="text-danger small mt-1">
                {updateRolesFormik.errors.role_ids}
              </div>
            )}
          </div>

          <div className="text-end">
            <Button
              color="secondary"
              outline
              type="button"
              className="me-2"
              onClick={() => setUpdateRolesOpen(false)}
            >
              Cancel
            </Button>
            <Button color="info" type="submit" disabled={updatingRoles}>
              {updatingRoles ? "Saving..." : "Save Roles"}
            </Button>
          </div>
        </Form>
      </ThemeModal>

      {/* Revoke Confirmation Modal */}
      <ThemeModal
        isOpen={revokeOpen}
        toggle={() => setRevokeOpen(false)}
        title="Revoke System Access"
      >
        <Alert color="danger">
          This will immediately disable login for{" "}
          <strong>{linkedUser?.username}</strong>. The HR record stays intact —
          you can re-grant access later. All active sessions will be terminated.
        </Alert>
        <div className="text-end">
          <Button
            color="secondary"
            outline
            type="button"
            className="me-2"
            onClick={() => setRevokeOpen(false)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleRevoke} disabled={revoking}>
            {revoking ? "Revoking..." : "Revoke Access"}
          </Button>
        </div>
      </ThemeModal>

      {/* Reset Password Confirmation Modal */}
      <ThemeModal
        isOpen={resetPwOpen}
        toggle={() => setResetPwOpen(false)}
        title="Reset Password"
      >
        <Alert color="warning">
          This will generate a new password for{" "}
          <strong>{linkedUser?.username}</strong> and terminate all active
          sessions. The new password will be shown <strong>only once</strong>.
        </Alert>
        <div className="text-end">
          <Button
            color="secondary"
            outline
            type="button"
            className="me-2"
            onClick={() => setResetPwOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={handleResetPassword}
            disabled={resettingPw}
          >
            {resettingPw ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
      </ThemeModal>

      {/* Restore Access Modal */}
      <ThemeModal
        isOpen={restoreOpen}
        toggle={() => setRestoreOpen(false)}
        title="Restore System Access"
      >
        <Form
          onSubmit={e => {
            e.preventDefault()
            restoreFormik.handleSubmit()
          }}
        >
          <Alert color="info">
            Restoring access for <strong>{linkedUser?.username}</strong>. A new
            password will be generated. Select roles below.
          </Alert>

          <div className="mb-3">
            <Label>
              Roles <span className="text-danger">*</span>
            </Label>
            <Select
              isMulti
              options={roleOptions}
              styles={selectStyles}
              value={roleOptions.filter(o =>
                restoreFormik.values.role_ids.includes(o.value)
              )}
              onChange={selected =>
                restoreFormik.setFieldValue(
                  "role_ids",
                  selected ? selected.map(s => s.value) : []
                )
              }
            />
            {restoreFormik.errors.role_ids && (
              <div className="text-danger small mt-1">
                {restoreFormik.errors.role_ids}
              </div>
            )}
          </div>

          <div className="text-end">
            <Button
              color="secondary"
              outline
              type="button"
              className="me-2"
              onClick={() => setRestoreOpen(false)}
            >
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={granting}>
              {granting ? "Restoring..." : "Restore Access"}
            </Button>
          </div>
        </Form>
      </ThemeModal>

      {/* Credentials Display Modal — shown once after grant-access succeeds */}
      <ThemeModal
        isOpen={!!credentialsModal}
        toggle={() => setCredentialsModal(null)}
        title="Login Credentials"
        backdrop="static"
        keyboard={false}
      >
        <Alert color="warning">
          <i className="bx bx-shield-alt-2 me-1" />
          Save these credentials now — the password will not be shown again.
        </Alert>

        <div className="mb-3">
          <small className="text-muted d-block">Username</small>
          <div className="d-flex align-items-center">
            <code className="flex-grow-1 p-2 bg-light rounded">
              {credentialsModal?.username}
            </code>
            <Button
              color="link"
              onClick={() => copyToClipboard(credentialsModal?.username)}
            >
              <i className="bx bx-copy" />
            </Button>
          </div>
        </div>

        <div className="mb-3">
          <small className="text-muted d-block">Password</small>
          <div className="d-flex align-items-center">
            <code className="flex-grow-1 p-2 bg-light rounded">
              {credentialsModal?.password}
            </code>
            <Button
              color="link"
              onClick={() => copyToClipboard(credentialsModal?.password)}
            >
              <i className="bx bx-copy" />
            </Button>
          </div>
        </div>

        <div className="text-end">
          <Button color="primary" onClick={() => setCredentialsModal(null)}>
            I've saved these — Close
          </Button>
        </div>
      </ThemeModal>
    </>
  )
}

export default SystemAccessCard
